import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Spinner from "../components/Spinner/Spinner";
import { formatDateWithDay } from "../utils/dateUtils";
import {
  formatPhoneNumberForSms,
  formatPhoneNumber,
} from "../utils/phoneNumberUtils";
import { activeShuttleParticipants } from "../utils/shuttleParticipants";

const statusLabels = {
  not_checked_in: "Not checked in",
  waiting_at_pickup: "Waiting",
  picked_up: "Picked up",
  running_late: "Running late",
  cancelled: "Cancelled",
  no_show: "No-show",
  assigned: "Assigned",
  on_route_to_pickup: "On route",
  arrived_at_pickup: "Arrived",
  departed_pickup: "Departed",
  arrived_at_event: "At event",
  completed: "Completed",
};

const tokenKey = (code) => `shuttle-dashboard-token-${code}`;
const assignmentStatuses = [
  { value: "assigned", label: "Assigned" },
  { value: "on_route_to_pickup", label: "On route" },
  { value: "arrived_at_pickup", label: "Arrived" },
  { value: "departed_pickup", label: "Departed" },
  { value: "arrived_at_event", label: "At event" },
  { value: "completed", label: "Completed" },
];
const hideEtaStatuses = ["arrived_at_event", "completed"];
const studentStatusStyles = {
  not_checked_in: "border-gray-200 bg-gray-50",
  waiting_at_pickup: "border-blue-200 bg-blue-50",
  picked_up: "border-green-200 bg-green-50",
  running_late: "border-amber-200 bg-amber-50",
  cancelled: "border-red-200 bg-red-50 opacity-80",
  no_show: "border-red-300 bg-red-100",
};
const studentStatusIconStyles = {
  not_checked_in: "bg-gray-500 text-white",
  waiting_at_pickup: "bg-blue-600 text-white",
  picked_up: "bg-green-600 text-white",
  running_late: "bg-amber-500 text-white",
  cancelled: "bg-red-500 text-white",
  no_show: "bg-red-700 text-white",
};
const studentStatusIcons = {
  not_checked_in: "–",
  waiting_at_pickup: "⏱",
  picked_up: "✓",
  running_late: "!",
  cancelled: "×",
  no_show: "!",
};

const ShuttleDashboard = () => {
  const { code } = useParams();
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const userToken = localStorage.getItem("userToken");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(
    () => localStorage.getItem(tokenKey(code)) || "",
  );
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(token || userToken));
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(!(token || userToken));

  const fetchDashboard = useCallback(async () => {
    if (!token && !userToken) return;

    try {
      setError("");
      const response = await fetch(
        `${apiBaseUrl}/api/shuttle-dashboard/${code}`,
        {
          headers: {
            ...(token ? { "X-Shuttle-Dashboard-Token": token } : {}),
            ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
          },
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (response.status === 401) {
        if (token) {
          localStorage.removeItem(tokenKey(code));
          setToken("");
        }
        if (!userToken) {
          setLocked(true);
          setData(null);
          setError(payload.message || "Dashboard password is required.");
        } else {
          setLocked(true);
          setError(
            "Your login could not unlock this dashboard. Enter the dashboard password.",
          );
        }
        return;
      }
      if (!response.ok) {
        throw new Error(payload.message || "Unable to load dashboard.");
      }
      setData(payload);
      setLocked(false);
    } catch (err) {
      setError(err.message || "Unable to load dashboard.");
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, code, token, userToken]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!token && !userToken) return undefined;
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard, token, userToken]);

  const updateShuttleStatus = async (assignmentId, status) => {
    try {
      setIsUpdatingStatus(true);
      setError("");
      const response = await fetch(
        `${apiBaseUrl}/api/shuttle-dashboard/${code}/assignments/${assignmentId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { "X-Shuttle-Dashboard-Token": token } : {}),
            ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
          },
          body: JSON.stringify({ status }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to update shuttle status.");
      }
      setData(payload);
    } catch (err) {
      setError(err.message || "Unable to update shuttle status.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    try {
      setIsUnlocking(true);
      setError("");
      const response = await fetch(
        `${apiBaseUrl}/api/shuttle-dashboard/${code}/unlock`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to unlock dashboard.");
      }
      localStorage.setItem(tokenKey(code), payload.token);
      setToken(payload.token);
      setLocked(false);
      setPassword("");
    } catch (err) {
      setError(err.message || "Unable to unlock dashboard.");
    } finally {
      setIsUnlocking(false);
    }
  };

  const sortedSpots = useMemo(
    () =>
      (data?.pickupSpots || []).map((spot) => {
        const students = activeShuttleParticipants(spot.students);
        return {
          ...spot,
          students,
          counts: {
            ...spot.counts,
            assigned: students.length,
            waiting: students.filter(
              (student) => student.status === "waiting_at_pickup",
            ).length,
            pickedUp: students.filter(
              (student) => student.status === "picked_up",
            ).length,
            runningLate: students.filter(
              (student) => student.status === "running_late",
            ).length,
          },
        };
      }),
    [data],
  );

  const visibleCounts = useMemo(() => {
    const students = sortedSpots.flatMap((spot) => spot.students);
    return {
      assigned: students.length,
      waiting: students.filter((student) => student.status === "waiting_at_pickup")
        .length,
      pickedUp: students.filter((student) => student.status === "picked_up")
        .length,
      runningLate: students.filter((student) => student.status === "running_late")
        .length,
    };
  }, [sortedSpots]);

  if (locked) {
    return (
      <DashboardShell>
        <div className="mx-auto max-w-md rounded-3xl bg-white p-6 shadow-2xl">
          <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
            Shuttle dashboard
          </p>
          <h1 className="mt-2 text-2xl font-black text-gray-900">
            Password required
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Enter the event dashboard password to view shuttle status.
          </p>
          {error && (
            <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">
              {error}
            </div>
          )}
          <form onSubmit={unlock} className="mt-5 space-y-3">
            <input
              type="password"
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
              placeholder="Dashboard password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              className="w-full rounded-xl bg-[#004a7e] px-4 py-3 text-sm font-black text-white"
              disabled={isUnlocking}
            >
              {isUnlocking ? "Unlocking..." : "Unlock dashboard"}
            </button>
          </form>
        </div>
      </DashboardShell>
    );
  }

  if (isLoading && !data) {
    return <Spinner />;
  }

  return (
    <DashboardShell>
      <div className="mx-auto max-w-6xl space-y-4">
        {error && (
          <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        <div className="rounded-3xl bg-white/95 p-5 shadow-2xl">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
                Public shuttle dashboard
              </p>
              <h1 className="mt-1 text-2xl font-black text-gray-900 sm:text-3xl">
                {data?.event?.title}
              </h1>
              <p className="mt-1 text-sm font-semibold text-gray-600">
                {formatDateWithDay(data?.event?.dateOfOccurrence)}
              </p>
              {data?.event?.location && (
                <p className="mt-1 text-sm text-[#004a7e]">
                  Destination: {data.event.location}
                </p>
              )}
            </div>
            <div className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-600">
              Last updated:{" "}
              {data?.event?.lastUpdatedAt
                ? new Date(data.event.lastUpdatedAt).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })
                : "—"}
              <br />
              Auto-refreshes every 30 seconds
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7">
            <Stat label="Assigned" value={visibleCounts.assigned} />
            <Stat
              label="Waiting"
              value={visibleCounts.waiting}
              tone="blue"
            />
            <Stat
              label="Picked up"
              value={visibleCounts.pickedUp}
              tone="green"
            />
            <Stat
              label="Late"
              value={visibleCounts.runningLate}
              tone="amber"
            />
            <Stat
              label="No-show"
              value={data?.counts?.noShow || 0}
              tone="red"
            />
            <Stat
              label="Active"
              value={data?.counts?.activeShuttles || 0}
              tone="blue"
            />
            <Stat
              label="Done"
              value={data?.counts?.completedShuttles || 0}
              tone="green"
            />
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {sortedSpots.map((spot) => (
            <PickupSpotCard
              key={spot.id}
              spot={spot}
              onShuttleStatusChange={updateShuttleStatus}
              isUpdatingStatus={isUpdatingStatus}
            />
          ))}
        </div>

        {sortedSpots.length === 0 && (
          <div className="rounded-2xl bg-white p-6 text-center text-gray-600 shadow-lg">
            No pickup spots are available yet.
          </div>
        )}
      </div>
    </DashboardShell>
  );
};

const DashboardShell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-[#00385f] via-[#004a7e] to-[#0f766e] px-3 py-5 sm:px-5">
    {children}
  </div>
);

const Stat = ({ label, value, tone = "default" }) => {
  const toneClass = {
    default: "text-gray-900",
    blue: "text-[#004a7e]",
    green: "text-green-700",
    amber: "text-amber-700",
    red: "text-red-700",
  }[tone];

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 text-center shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className={`text-xl font-black ${toneClass}`}>{value}</p>
    </div>
  );
};

const PickupSpotCard = ({ spot, onShuttleStatusChange, isUpdatingStatus }) => {
  const [showStudents, setShowStudents] = useState(false);

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-xl">
      <div className="border-b border-gray-100 bg-gray-50 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
              Pickup spot
            </p>
            <h2 className="mt-1 text-xl font-black text-gray-900">
              {spot.name}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {spot.address || "No address set"}
            </p>
            {spot.pickupTime && (
              <p className="mt-1 text-xs font-bold text-[#004a7e]">
                Pickup: {spot.pickupTime}
              </p>
            )}
          </div>
          <div className="rounded-xl bg-white px-3 py-2 text-center shadow-sm">
            <p className="text-lg font-black text-gray-900">
              {spot.counts.assigned}
            </p>
            <p className="text-[10px] font-bold uppercase text-gray-500">
              Students
            </p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <MiniCount label="Waiting" value={spot.counts.waiting} />
          <MiniCount label="Picked" value={spot.counts.pickedUp} />
          <MiniCount label="Late" value={spot.counts.runningLate} />
        </div>
      </div>

      <div className="space-y-4 p-4">
        <section>
          <h3 className="text-sm font-black text-gray-900">Shuttles</h3>
          <div className="mt-2 space-y-2">
            {spot.shuttles.length === 0 && (
              <p className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
                No shuttles assigned.
              </p>
            )}
            {spot.shuttles.map((shuttle) => (
              <div
                key={shuttle.id}
                className="rounded-xl border border-gray-100 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {shuttle.driverPhone ? (
                      <a
                        href={`sms:${formatPhoneNumberForSms(shuttle.driverPhone)}`}
                        className="font-black text-gray-900 underline decoration-gray-300 underline-offset-2"
                      >
                        {shuttle.driverName || "Volunteer"}
                      </a>
                    ) : (
                      <p className="font-black text-gray-900">
                        {shuttle.driverName || "Volunteer"}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      {shuttle.vehicleDescription || "Vehicle not set"}
                    </p>
                    <p className="mt-1 text-xs font-bold text-[#004a7e]">
                      {statusLabels[shuttle.status] || shuttle.status}
                    </p>
                  </div>
                  {!hideEtaStatuses.includes(shuttle.status) && (
                    <div className="rounded-xl bg-indigo-50 px-3 py-2 text-center">
                      <p className="text-lg font-black text-indigo-900">
                        {shuttle.etaMinutes ? `${shuttle.etaMinutes}m` : "—"}
                      </p>
                      <p className="text-[10px] font-bold uppercase text-indigo-700">
                        ETA
                      </p>
                    </div>
                  )}
                </div>
                <select
                  className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-gray-800"
                  value={shuttle.status}
                  disabled={isUpdatingStatus}
                  onChange={(e) =>
                    onShuttleStatusChange(shuttle.id, e.target.value)
                  }
                >
                  {assignmentStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </section>

        <section>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-xl bg-gray-50 px-3 py-2 text-left text-sm font-black text-gray-900"
            onClick={() => setShowStudents(!showStudents)}
          >
            <span>Students ({spot.students.length})</span>
            <span>{showStudents ? "Hide" : "Show"}</span>
          </button>
          {showStudents && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {spot.students.length === 0 && (
                <p className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
                  No students assigned.
                </p>
              )}
              {spot.students.map((student, index) => (
                <div
                  key={`${student.name}-${index}`}
                  className={`rounded-xl border p-3 ${studentStatusStyles[student.status] || "border-gray-100 bg-white"}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-black ${studentStatusIconStyles[student.status] || "bg-gray-500 text-white"}`}
                      title={statusLabels[student.status] || student.status}
                      aria-label={
                        statusLabels[student.status] || student.status
                      }
                    >
                      {studentStatusIcons[student.status] || "–"}
                    </span>
                    {student.phone ? (
                      <a
                        href={`sms:${formatPhoneNumberForSms(student.phone)}`}
                        className="font-bold text-gray-900 underline decoration-gray-300 underline-offset-2"
                      >
                        {student.name}
                      </a>
                    ) : (
                      <p className="font-bold text-gray-900">{student.name}</p>
                    )}
                  </div>
                  {student.phone && (
                    <p className="ml-5 text-xs text-gray-500">
                      {formatPhoneNumber(student.phone)}
                    </p>
                  )}
                  {student.runningLateMinutes ? (
                    <p className="ml-5 text-xs font-bold text-amber-700">
                      {student.runningLateMinutes} min late
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const MiniCount = ({ label, value }) => (
  <div className="rounded-lg bg-white p-2 text-center shadow-sm">
    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="text-lg font-black text-gray-900">{value}</p>
  </div>
);

export default ShuttleDashboard;
